
import {of as observableOf,  Observable } from 'rxjs';

import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ActivateAgendaPage, AddAgendaPage, DeactivateAgendaPage, UpdateAgendaPage } from '@routes/events/models/event-pages/agendapage-model';
import { ActivateInformationPage, AddInformationPage, DeactivateInformationPage, UpdateInformationPage } from '@routes/events/models/event-pages/informationpage-model';
import { AddRegistrationPage, UpdateRegistrationPage } from '@routes/events/models/event-pages/registrationpage-model';
import { IAgendaPage, IInformationPage, IRegistrationPage } from '@shared/services/api/legacy/eventpages-service/eventpage-model';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';
import { ActivateSurveyPage, DeactivateSurveyPage, IAddEventSurveyPage, IEventSurveyPage, ISurveyStatistics, IUpdateEventSurveyPage } from '../survey-service/survey-model';
import { ICustomPage, AddCustomPage, UpdateCustomPage, DeactivateCustomPage, ActivateCustomPage } from '@routes/events/models/event-pages/custompage-model';
import { IWebinarPage, AddWebinarPage, UpdateWebinarPage } from '@routes/events/models/event-pages/webinarpage-model';

@Injectable()
export class EventPagesService {
    constructor(private http: HttpClient) { }

    private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${EventPagesService.ENDPOINT}`; }
    private static readonly ENDPOINT = '/timelines/{timelineId}';

    /** Registration page api calls. */

    public getRegistrationPage(id: string, timelineId: string): Observable<SuccessResponse<IRegistrationPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IRegistrationPage>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/registrationpages/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addRegistrationPage(model: AddRegistrationPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/registrationpages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateRegistrationPage(model: UpdateRegistrationPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/registrationpages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Agenda page api calls. */

    public getAgendaPage(id: string, timelineId: string): Observable<SuccessResponse<IAgendaPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IAgendaPage>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/agendapages/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addAgendaPage(model: AddAgendaPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/agendapages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateAgendaPage(model: UpdateAgendaPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/agendapages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateAgendaPage(model: DeactivateAgendaPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/agendapages/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateAgendaPage(model: ActivateAgendaPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/agendapages/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Information page api calls. */

    public getInformationPage(id: string, timelineId: string): Observable<SuccessResponse<IInformationPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IInformationPage>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/informationpages/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addInformationPage(model: AddInformationPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/informationpages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateInformationPage(model: UpdateInformationPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/informationpages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateInformationPage(model: DeactivateInformationPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/informationpages/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateInformationPage(model: ActivateInformationPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/informationpages/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Custom page api calls. */

    public getCustomPage(id: string, timelineId: string): Observable<SuccessResponse<ICustomPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<ICustomPage>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/custompages/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addCustomPage(model: AddCustomPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/custompages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateCustomPage(model: UpdateCustomPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/custompages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateCustomPage(model: DeactivateCustomPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/custompages/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateCustomPage(model: ActivateCustomPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/custompages/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Webinar welcome page api calls. */

    public getWebinarWelcomePage(id: string, timelineId: string): Observable<SuccessResponse<IWebinarPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IWebinarPage>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/webinarwelcomepages/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addWebinarWelcomePage(model: AddWebinarPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/webinarwelcomepages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateWebinarWelcomePage(model: UpdateWebinarPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/webinarwelcomepages/' + model.id, model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Webinar thank you page api calls. */

    public getWebinarThankYouPage(id: string, timelineId: string): Observable<SuccessResponse<IWebinarPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IWebinarPage>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/webinarthankyoupages/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addWebinarThankYouPage(model: AddWebinarPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/webinarthankyoupages/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateWebinarThankYouPage(model: UpdateWebinarPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/webinarthankyoupages/' + model.id, model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** Survey page api calls. */

    public getEventSurveyPage(id: string, timelineId: string): Observable<SuccessResponse<IEventSurveyPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IEventSurveyPage>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/surveypage/' + id).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    /** GET survey page by type (event page type: 4/5/6), including its event-level questions. Used by the
     *  add flow to preload existing questions before a page id exists, so they are visible/editable in both
     *  the old and new app. Returns questions with an empty page id when no page shell exists yet. */
    public getEventSurveyPageByType(type: number, timelineId: string): Observable<SuccessResponse<IEventSurveyPage> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IEventSurveyPage>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/surveypage/by-type/' + type).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public addEventSurveyPage(model: IAddEventSurveyPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
        .post<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/surveypage/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public updateEventSurveyPage(model: IUpdateEventSurveyPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
        .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/surveypage/', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public deactivateSurveyPage(model: DeactivateSurveyPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/surveypage/deactivate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public activateSurveyPage(model: ActivateSurveyPage, timelineId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/surveypage/activate', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getSurveyStatictics(timelineId: string, surveyPageId: string): Observable<SuccessResponse<ISurveyStatistics> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<ISurveyStatistics>>(EventPagesService.URL.replace('{timelineId}', timelineId) + '/surveypage/stats/' + surveyPageId).pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }
}
